/**
 * The audition clip of a visible Voice of the graph (ADR-0068), played wherever the voice is
 * shown: the graph sidebar's card, the investigation sidebar and the entity sheet of the
 * Research page. The clip is the speech of that one voice, cut out by the audio worker, behind
 * the audited clip route a viewer may read. Where there is nothing to play the card's status
 * line already says why (too little speech, not prepared yet); what this adds is the HUMINT
 * gate and, after a fetch that failed, the server's reason in words.
 */
import { useTranslation } from 'react-i18next'
import type { AudioEvidence } from '@/components/investigations/entity-detail/audio-evidence'
import { type AudioFailureText, AudioPlayback } from '@/features/identities/audio/AudioPlayback'
import { HUMINT_EXTENSION_ID, useExtensionEnabled } from '@/hooks/use-extensions'
import { audioFailureOf, voiceClipState } from './voice-clip'

const COPY = 'investigations.entityDetail.audio.clip'

/** The clip is always the WAV the worker wrote; the route itself names no suffix. */
export const CLIP_TYPED_AS = 'clip.wav'

export function useVoiceClipFailureText(): (error: unknown) => AudioFailureText {
  const { t } = useTranslation()
  return error => {
    const failure = audioFailureOf(error)
    if (failure === 'other') return { text: t('identity.detail.audio.playFailed'), retry: true }
    return { text: t(`${COPY}.${failure}`), retry: false }
  }
}

/** One voice clip by its audited route: the Voices card and the Unassigned voices tab play through it. */
export function VoiceClipPlayback({
  url,
  typedAs = CLIP_TYPED_AS,
}: {
  url: string
  typedAs?: string
}) {
  const failureText = useVoiceClipFailureText()
  return <AudioPlayback url={url} typedAs={typedAs} failureText={failureText} />
}

export function VoiceClipPlayer({ caseId, evidence }: { caseId: string; evidence: AudioEvidence }) {
  const { t } = useTranslation()
  const humint = useExtensionEnabled(HUMINT_EXTENSION_ID)
  const failureText = useVoiceClipFailureText()
  const state = voiceClipState(caseId, evidence, humint)
  if (state.kind === 'ready')
    return (
      <AudioPlayback url={state.url} typedAs={CLIP_TYPED_AS} failureText={failureText} size="xs" />
    )
  if (state.kind === 'humint_off')
    return <p className="text-xs text-muted-foreground">{t(`${COPY}.humintOff`)}</p>
  return null
}
